// Pure serialization of the partner-offer list filters into PostgREST operations.
// Kept framework-free (no supabase import) so BOTH the on-screen list query and
// the paginated CSV export apply IDENTICAL filters, and so the mapping is unit-
// testable in isolation. The service turns each {op, args} into a builder call.
import type {
  PartnerOffer,
  PartnerOfferFilters,
} from "../types/partnerOffer.types";

export type PartnerOfferFilterOp = {
  op: "eq" | "is" | "or" | "contains";
  args: [string, unknown] | [string];
};

// Sentinel used by the "US Law Shield (Internal)" source-org filter option to
// mean "no source organization". It must NOT be passed to .eq() on a uuid column
// (Postgres 22P02 → the whole list load fails); the service maps it to IS NULL.
export const INTERNAL_SOURCE_SENTINEL = "internal";

// The partner identity shown/searched/sorted in the UI. company_name is optional
// now (FR-03), so the source organization's name is the effective identity.
export function effectiveCompanyName(
  offer: Pick<PartnerOffer, "company_name" | "source_organization">,
): string {
  return (
    offer.company_name || offer.source_organization?.name || "US Law Shield"
  );
}

// Case-insensitive comparator on the effective company name, for the "Company"
// column sort. Server-side ORDER BY can't reach the joined org name, so the
// service applies this to the fetched rows.
export function compareByEffectiveCompany(
  a: Pick<PartnerOffer, "company_name" | "source_organization">,
  b: Pick<PartnerOffer, "company_name" | "source_organization">,
  ascending: boolean,
): number {
  const cmp = effectiveCompanyName(a).localeCompare(
    effectiveCompanyName(b),
    undefined,
    { sensitivity: "base" },
  );
  return ascending ? cmp : -cmp;
}

// `matchingSourceOrgIds` — organization ids whose name matched the search term,
// resolved by the service. Included so a search for the DISPLAYED partner name
// (which for new offers lives on the source org, not company_name) finds the offer.
export function partnerOfferFilterOps(
  filters?: PartnerOfferFilters,
  matchingSourceOrgIds?: string[],
): PartnerOfferFilterOp[] {
  const ops: PartnerOfferFilterOp[] = [];
  if (filters?.status) {
    ops.push({ op: "eq", args: ["status", filters.status] });
  }
  if (filters?.category_id) {
    ops.push({ op: "eq", args: ["category_id", filters.category_id] });
  }
  if (filters?.source_organization_id) {
    if (filters.source_organization_id === INTERNAL_SOURCE_SENTINEL) {
      ops.push({ op: "is", args: ["source_organization_id", null] });
    } else {
      ops.push({
        op: "eq",
        args: ["source_organization_id", filters.source_organization_id],
      });
    }
  }
  if (filters?.offer_kind) {
    ops.push({ op: "eq", args: ["offer_kind", filters.offer_kind] });
  }
  if (filters?.brand_target) {
    ops.push({ op: "eq", args: ["brand_target", filters.brand_target] });
  }
  if (filters?.is_featured !== undefined) {
    ops.push({ op: "eq", args: ["is_featured", filters.is_featured] });
  }
  if (filters?.search) {
    let orFragment = `company_name.ilike.*${filters.search}*,headline.ilike.*${filters.search}*,tags.cs.{${filters.search}}`;
    // Also match offers whose SOURCE ORG name matched the term (their displayed
    // company). IDs are UUIDs (safe to interpolate); the ilike term is
    // pre-existing raw interpolation, unchanged here.
    if (matchingSourceOrgIds && matchingSourceOrgIds.length > 0) {
      orFragment += `,source_organization_id.in.(${matchingSourceOrgIds.join(",")})`;
    }
    ops.push({ op: "or", args: [orFragment] });
  }
  if (filters?.tags && filters.tags.length > 0) {
    ops.push({ op: "contains", args: ["tags", filters.tags] });
  }
  return ops;
}
